/**
 * Utility functions for handling educational standard sets
 * Used by lesson dropdown components to parse, normalize, and display standards
 */

/**
 * Extract standard set identifier from a lesson's standard string
 * @param {string} standardName - The standard string (e.g., "TX.4.1.A", "CCSS.MATH.1.OA.A.1")
 * @returns {string|null} - The standard set identifier (e.g., "TX", "CCSS") or null if invalid
 */
export const getStandardSetFromStandard = (standardName) => {
  if (!standardName || standardName === "N/A") return null;

  const standardUpper = standardName.toUpperCase();

  // Handle specific known patterns
  if (standardUpper.startsWith("SC.")) return "SC";
  if (standardUpper.startsWith("CCSS.") || standardUpper.startsWith("CCSS ")) return "CCSS";
  if (standardUpper.startsWith("TX.")) return "TX";
  if (standardUpper.startsWith("CA.")) return "CA";
  if (standardUpper.startsWith("FL.")) return "FL";
  if (standardUpper.startsWith("NY.")) return "NY";

  // Generic parsing - extract prefix before first dot or space
  const firstPart = standardName.split(/[.\s]/)[0].toUpperCase();

  if (firstPart === "CCSS") return "CCSS";
  if (firstPart.length >= 2 && firstPart.length <= 4) {
    return firstPart;
  }

  // Default fallback
  return "CCSS";
};

/**
 * Get display name for a standard set
 * @param {string} shortname - The standard set shortname (e.g., "TX", "CCSS")
 * @returns {string} - The display name (e.g., "Texas Standards", "National Standards")
 */
export const getStandardSetDisplayName = (shortname) => {
  const standardSetNames = {
    SC: "SC State Standards",
    CCSS: "National Standards",
    TX: "Texas Standards",
    CA: "California Standards",
    FL: "Florida Standards",
    NY: "New York Standards",
  };

  return standardSetNames[shortname] || `${shortname} Standards`;
};

/**
 * Normalize standard set names for consistency
 * @param {string} shortname - The standard set shortname
 * @returns {string} - The normalized shortname
 */
export const normalizeStandardSet = (shortname) => {
  if (shortname === "NATL") return "CCSS";
  return shortname;
};

/**
 * Group lessons by their standard sets for ClassTab component
 * @param {Array} lessons - Array of lesson groups with options
 * @param {string} preferredStandardSet - Preferred standard set to show first
 * @returns {Array} - Grouped lessons by standard set
 */
export const groupLessonsByStandardSet = (lessons, preferredStandardSet = null) => {
  const standardSetGroups = {};

  lessons.forEach((lessonGroup) => {
    if (lessonGroup.options) {
      lessonGroup.options.forEach((lesson) => {
        // Determine standard set based on the first few letters of the standard field
        let standardSetKey = "Other"; // Default fallback
        let displayName = "Other";

        if (lesson.standard) {
          const standardUpper = lesson.standard.toUpperCase();

          if (standardUpper.startsWith("CCSS")) {
            // Special case for Common Core (National standards)
            standardSetKey = "National";
            displayName = "National";
          } else {
            // For all other standards, extract the prefix before the first dot
            const firstDotIndex = standardUpper.indexOf(".");
            if (firstDotIndex !== -1) {
              // Use everything before the first dot as the standard set
              const prefix = standardUpper.substring(0, firstDotIndex);
              standardSetKey = prefix;
              displayName = prefix;
            } else {
              // If no dot found, use the first 2-3 characters
              const shortPrefix = standardUpper.substring(0, 2);
              standardSetKey = shortPrefix;
              displayName = shortPrefix;
            }
          }
        }

        if (!standardSetGroups[standardSetKey]) {
          standardSetGroups[standardSetKey] = [];
        }

        // Add the group property to each lesson for the custom styles to work
        const lessonWithGroup = {
          ...lesson,
          group: displayName,
        };

        standardSetGroups[standardSetKey].push(lessonWithGroup);
      });
    }
  });

  // Convert to the format expected by react-select
  // Sort so preferred standard set comes first, then National, then others alphabetically
  const sortedKeys = Object.keys(standardSetGroups).sort((a, b) => {
    // Determine preferred standard set display name
    const preferredDisplayName =
      preferredStandardSet === "NATL" ? "National" : preferredStandardSet;

    // If we have a preferred standard set, put it first
    if (preferredDisplayName && a === preferredDisplayName) return -1;
    if (preferredDisplayName && b === preferredDisplayName) return 1;

    // Then put National second (if it's not already the preferred)
    if (a === "National" && preferredDisplayName !== "National") return -1;
    if (b === "National" && preferredDisplayName !== "National") return 1;

    // All others alphabetically
    return a.localeCompare(b);
  });

  const groupedOptions = sortedKeys.map((standardSetKey) => ({
    label: standardSetKey,
    options: standardSetGroups[standardSetKey],
  }));

  return groupedOptions;
};

/**
 * Extract available standard sets from grouped lessons for LearnerTab component
 * @param {Array} groupedLessons - Array of lesson groups with options
 * @returns {Array} - Array of available standard sets with metadata
 */
export const extractAvailableStandardSets = (groupedLessons) => {
  if (!groupedLessons || groupedLessons.length === 0) {
    return [];
  }

  const standardSetMap = new Map();

  groupedLessons.forEach((group) => {
    if (group.options) {
      group.options.forEach((lesson) => {
        let standardSetShortname = lesson.standardset;

        if (!standardSetShortname || standardSetShortname === "N/A") {
          standardSetShortname = getStandardSetFromStandard(lesson.standard);
        }

        if (standardSetShortname && standardSetShortname !== "N/A") {
          const normalizedStandardSet = normalizeStandardSet(standardSetShortname);

          if (!standardSetMap.has(normalizedStandardSet)) {
            const displayName = getStandardSetDisplayName(normalizedStandardSet);
            standardSetMap.set(normalizedStandardSet, {
              shortname: normalizedStandardSet,
              displayName: displayName,
              lessonCount: 1,
            });
          } else {
            standardSetMap.get(normalizedStandardSet).lessonCount++;
          }
        }
      });
    }
  });

  const standardSets = Array.from(standardSetMap.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );

  return standardSets;
};

/**
 * Filter lessons by standard set for LearnerTab component
 * @param {Array} groupedLessons - Array of lesson groups with options
 * @param {string} selectedStandardSet - The standard set to filter by
 * @returns {Array} - Filtered lesson groups
 */
export const filterLessonsByStandardSet = (groupedLessons, selectedStandardSet) => {
  if (!selectedStandardSet || !groupedLessons) {
    return [];
  }

  const filtered = groupedLessons
    .map((group) => {
      const filteredOptions = group.options.filter(
        (lesson) => lesson.standardset === selectedStandardSet
      );

      return filteredOptions.length > 0
        ? {
            ...group,
            options: filteredOptions,
          }
        : null;
    })
    .filter(Boolean);

  return filtered;
};

/**
 * Get lessons for a specific standard set for LearnerTab component
 * @param {Array} groupedLessons - Array of lesson groups with options
 * @param {string} standardSetShortname - The standard set shortname
 * @returns {Array} - Lessons for the specified standard set
 */
export const getLessonsForStandardSet = (groupedLessons, standardSetShortname) => {
  if (!groupedLessons) return [];

  return groupedLessons
    .map((group) => {
      const filteredOptions = group.options.filter((lesson) => {
        let lessonStandardSet = lesson.standardset;

        if (!lessonStandardSet || lessonStandardSet === "N/A") {
          lessonStandardSet = getStandardSetFromStandard(lesson.standard);
        }

        const normalizedLessonStandardSet = normalizeStandardSet(lessonStandardSet);
        const normalizedTargetStandardSet = normalizeStandardSet(standardSetShortname);
        return normalizedLessonStandardSet === normalizedTargetStandardSet;
      });

      return filteredOptions.length > 0
        ? {
            ...group,
            options: filteredOptions,
          }
        : null;
    })
    .filter(Boolean);
};